import type { ReactElement } from 'react';

import { Button, type ButtonProps } from '@/ui/components/Button';

import type { ToolbarItem } from './types';

/**
 * One toolbar action — docs/DESIGN.md §6.2, ADR-0014.
 *
 * A thin adapter from `ToolbarItem` (data) to `Button` (presentation), and thin on purpose:
 * everything visual is already decided in `Button`, and everything semantic was decided in
 * the command registry. What is left here is the shortcut, the disabled state and the
 * accessible name — three things that are easy to drop silently and that this file makes
 * structural.
 *
 * ═══ EVERY ACTION PRINTS ITS OWN SHORTCUT ON ITSELF, AT REST ═══
 *
 * `shortcutHint` goes onto the control, not into a tooltip and not into a help modal
 * (docs/DESIGN.md §6.2). Below 1120px the printed key is hidden by CSS and `title` keeps it —
 * so the hint has to reach BOTH, which is why the caller passes a composed `title`
 * (`a11y.titleWithShortcut`) rather than the shell concatenating a label and a keycap. A
 * label and a keycap joined in JSX cannot be reordered by a translator, and the legacy build
 * lost exactly that argument by appending a literal " (Note: Invalid JSON)" to a translated
 * copy-success string — an English suffix no locale could ever reach.
 *
 * (That example is deliberately spelled out in prose rather than as a `t(…)` call: the usage
 * regex in `scripts/check-locale-keys.mjs` cannot tell a comment from code, so a key quoted
 * inside a comment fails the gate as a missing key. This one did.)
 *
 * ═══ WHY `aria-label` IS SET SEPARATELY FROM `label` ═══
 *
 * `Button` uses `label` as both the visible text and the accessible name. That is right until
 * the shortcut exists: a sighted user reads "Copy" and sees `⌘C` beside it, while a screen
 * reader should hear "Copy, Cmd C" as ONE name (`a11y.actionWithShortcut`). Passing
 * `aria-label` overrides the default — `Button` spreads `...rest` after its own attribute
 * precisely so this is possible — and when no composed name is given, `label` still applies,
 * so a button can never end up nameless.
 */

/**
 * `id`, `available`, `align` and `group` are the FIELDS THE BAR CONSUMES, not the button:
 * availability decides whether this component is called at all, the group decides where the
 * hairline falls, `align` decides which side of the spacer it lands on, and the id is the
 * React key. None of them survives into the DOM — there is deliberately no `data-command`
 * attribute, because a selector hook that exists only for tests is a second, silent contract
 * on the id, and the accessible name is already a stable, user-visible way to find a control.
 */
export type ToolbarButtonProps = Omit<ToolbarItem, 'id' | 'available' | 'align' | 'group'> & {
  /**
   * The bar's own layout classes (`.item`, `.primaryItem`). It is the one thing the toolbar
   * says about a button that `Button` does not decide for itself: how much of the dock's row
   * this control takes (ADR-0101 §7.6).
   */
  readonly className?: string | undefined;
};

export function ToolbarButton({
  label,
  icon,
  variant = 'tinted',
  hue,
  compactLabel,
  badge,
  shortcutHint,
  title,
  ariaLabel,
  disabled,
  className,
  onInvoke,
}: ToolbarButtonProps): ReactElement {
  // Built as an object rather than written inline because `exactOptionalPropertyTypes` makes
  // `shortcutHint={undefined}` a type error, not a no-op: an optional property either is
  // present or is not.
  const props: ButtonProps = {
    label,
    icon,
    variant,
    onClick: onInvoke,
    disabled: disabled === true,
    ...(hue === undefined ? {} : { hue }),
    ...(compactLabel === undefined ? {} : { compactLabel }),
    ...(badge === undefined ? {} : { badge }),
    ...(className === undefined ? {} : { className }),
    ...(shortcutHint === undefined ? {} : { shortcutHint }),
    ...(title === undefined ? {} : { title }),
    ...(ariaLabel === undefined ? {} : { 'aria-label': ariaLabel }),
  };

  return <Button {...props} />;
}

export default ToolbarButton;
